// Společné hlídání rozepsaných formulářů (05/412, rozhodnutí zadavatele 23. 9. 2026).
//
// Proč: aplikace dosud nikde nesledovala, že má někdo rozepsaný formulář. Zavření záložky, obnovení
// stránky i nasazení nové verze tak tiše zahodily hodinu práce (dispečer v zakázce, účetní v přijaté
// faktuře, vážný na vážním lístku). Pruh nové verze uměl jen obecně varovat, že „se něco ztratí“.
//
// Jak to funguje: obrazovka s delším formulářem se přihlásí hookem `useRozepsano(popis)` — popis je
// věta do výčtu („zakázka ZAK‑2026‑001“, „nový zaměstnanec“), nebo `null`, když rozepsané nic není.
// **Rozepsané = uživatel něco vyplnil a neuložil**; formulář jen otevřený se nepočítá, jinak by
// varování otravovalo pokaždé.
//
// Z toho pak plyne:
//   - `beforeunload` — zavření záložky nebo okna vyvolá nativní varování prohlížeče (text si píše
//     prohlížeč sám, aplikace do něj mluvit nemůže);
//   - pruh nové verze (`pwa/PruhNoveVerze.tsx`) rozepsané formuláře vyjmenuje v dialogu a nabídne
//     Obnovit později; u kritického nasazení (05/414) odpočet běží dál, jen text řekne, co se ztratí;
//   - **přechod na jinou obrazovku uvnitř aplikace** (lišta, odkaz, tlačítko Zpět prohlížeče) se zeptá
//     aplikačním dialogem „Máte rozepsaný formulář… Zahodit / Zůstat“ (rozhodnutí zadavatele 24. 9. 2026,
//     05/473) — `HlidacOdchodu` s `useBlocker` React Routeru. Hlídá jen formuláře, které se přihlásily
//     (jedenáct dlouhých z 05/412); krátké formuláře a zásuvky se nehlídají. Blokuje se jen přechod na jinou
//     **cestu** (jiná obrazovka), a to ten, který udělá člověk (odkaz, lišta, Zpět) — ne přesměrování, které si
//     aplikace dělá sama s nahrazením historie (`replace`: po uložení nové zakázky, po zavření zásuvky otevřené
//     odkazem); parametry adresy (`?faktura=`, filtry) taky ne — zásuvky se jimi otevírají a zavírají a zavření
//     zásuvky se nikde neptá (05/837 rozhodnuto 27. 9. 2026: tak). `useBlocker` potřebuje datový router — aplikace ho má od 25. 9. 2026
//     (`App.tsx`, `createBrowserRouter`); pod obyčejným `MemoryRouter` (testy obrazovek) hlídač mlčí.
//   - obrazovka, která se ukládá sama (nabídka na jedné stránce, `useZnackaSUlozenim`), dá hlídači i funkci „uložit
//     hned“: při odchodu ji hlídač zavolá **dřív, než se zeptá** — povede‑li se a nic rozepsaného nezůstalo, navigace
//     projde bez dialogu; jinak (výpadek, hodnota, kterou nejde uložit) se zeptá jako u ostatních formulářů. Na uložení
//     čeká nejvýš `LIMIT_ULOZENI_PRED_ODCHODEM_MS` (slabý signál, uvázlý požadavek) — pak se zeptá taky; Zahodit
//     zavolá „zahodit“ obrazovky, aby po odchodu neodešlo nic dalšího.
//   - **okna (Modal, Zasuvka)** se hlídají samy (rozhodnutí zadavatele 27. 10. 2026): kdo do okna něco napsal nebo
//     zvolil, a okno zavře křížkem nebo Escape, dostane otázku „Zahodit rozepsané?“ (`useRozepsaneOkno`). Okno se
//     přitom hlásí i hlídači výše, takže ho chrání také Zpět prohlížeče a zavření záložky. Okno, které ví líp, co je
//     rozepsané, to řekne vlastností `rozepsano` (popis, nebo `null` = nic). Tlačítko Zrušit je vědomé rozhodnutí,
//     neptá se.
//
// Bez `PoskytovatelRozepsanych` nad sebou hook nic nedělá (testy obrazovek ho nemusí řešit).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { type BlockerFunction, UNSAFE_DataRouterContext, useBlocker } from 'react-router';
import { poslouchejZapisy } from '../api/klient';
import { useDialog } from '../komponenty/Dialog';

/** Obrazovka, která se ukládá sama: „uložit hned“ (`true` = uloženo) a „zahodit“ (Zahodit v dialogu hlídače). */
interface UkladaniObrazovky {
  ulozit: () => Promise<boolean>;
  zahodit?: () => void;
}

/** Jak dlouho hlídač odchodu čeká na „uložit hned“ (ms) — pak se zeptá, i když ukládání ještě běží (uvázlý požadavek
 * na slabém signálu by jinak odchod držel bez odezvy, klient API limit nemá). */
export const LIMIT_ULOZENI_PRED_ODCHODEM_MS = 3000;

interface KontextRozepsanych {
  /** Popisy rozepsaných formulářů v pořadí, jak se přihlásily. */
  popisy: readonly string[];
  /** `ukladani` = obrazovka umí rozepsané uložit sama — hlídač odchodu to zkusí dřív, než se zeptá. */
  prihlasit: (id: string, popis: string, ukladani?: UkladaniObrazovky) => void;
  odhlasit: (id: string) => void;
  /**
   * Co je rozepsané **právě teď** (čte se z mapy, ne ze stavu) — hlídač odchodu se ptá v okamžiku navigace,
   * kdy by stav z posledního vykreslení mohl být o krok pozadu (uložení a hned přechod na novou obrazovku).
   */
  aktualni: () => readonly string[];
  /** „Uložit hned“ a „zahodit“ přihlášených obrazovek, které se ukládají samy (čte se v okamžiku navigace). */
  ukladani: () => readonly UkladaniObrazovky[];
}

const Kontext = createContext<KontextRozepsanych | null>(null);

/**
 * Odchod, na který se aplikace zeptala sama (nasazení nové verze) — nativní varování prohlížeče by
 * se pak ptalo podruhé. Platí do konce života stránky; po načtení je zase vypnuté.
 */
let odchodPovolen = false;

/** Aplikace se na odchod zeptala sama — `beforeunload` ať mlčí (volá `pouzitNovouVerzi()`). */
export function povolitOdchodBezVarovani(): void {
  odchodPovolen = true;
}

/** Jen pro testy: vrátí příznak odchodu do výchozího stavu. */
export function resetOdchoduProTesty(): void {
  odchodPovolen = false;
}

export function PoskytovatelRozepsanych({ children }: { children: ReactNode }) {
  const [popisy, setPopisy] = useState<readonly string[]>([]);
  // Zdroj pravdy je Map (pořadí vkládání = pořadí ve výčtu); stav je jen její kopie pro vykreslení.
  const zaznamy = useRef(new Map<string, string>());
  const ulozeni = useRef(new Map<string, UkladaniObrazovky>());

  const prihlasit = useCallback((id: string, popis: string, ukladani?: UkladaniObrazovky) => {
    if (ukladani) ulozeni.current.set(id, ukladani);
    else ulozeni.current.delete(id);
    if (zaznamy.current.get(id) === popis) return;
    zaznamy.current.set(id, popis);
    setPopisy([...zaznamy.current.values()]);
  }, []);

  const odhlasit = useCallback((id: string) => {
    ulozeni.current.delete(id);
    if (!zaznamy.current.delete(id)) return;
    setPopisy([...zaznamy.current.values()]);
  }, []);

  // Zavření záložky nebo okna: nativní varování prohlížeče (vlastní text do něj dát nelze).
  useEffect(() => {
    if (popisy.length === 0) return;
    const naOdchod = (udalost: BeforeUnloadEvent) => {
      if (odchodPovolen) return;
      udalost.preventDefault();
      // Starší prohlížeče potřebují ještě `returnValue`.
      udalost.returnValue = '';
    };
    window.addEventListener('beforeunload', naOdchod);
    return () => window.removeEventListener('beforeunload', naOdchod);
  }, [popisy.length]);

  const aktualni = useCallback(() => [...zaznamy.current.values()], []);
  const ukladani = useCallback(() => [...ulozeni.current.values()], []);

  const hodnota = useMemo<KontextRozepsanych>(
    () => ({ popisy, prihlasit, odhlasit, aktualni, ukladani }),
    [popisy, prihlasit, odhlasit, aktualni, ukladani],
  );

  return <Kontext.Provider value={hodnota}>{children}</Kontext.Provider>;
}

/**
 * Obrazovka hlásí, že má rozepsaný a neuložený formulář. `popis` je věta do výčtu (`'zakázka
 * ZAK‑2026‑001'`), `null` znamená „nic rozepsaného“ — hook se tedy volá vždy, mění se jen argument:
 *
 * ```tsx
 * useRozepsano(zmeneno ? `zakázka ${zakazka.cislo}` : null);
 * ```
 */
export function useRozepsano(popis: string | null | undefined): void {
  const kontext = useContext(Kontext);
  const id = useId();
  const prihlasit = kontext?.prihlasit;
  const odhlasit = kontext?.odhlasit;

  useEffect(() => {
    if (!prihlasit || !odhlasit || !popis) return;
    prihlasit(id, popis);
    return () => odhlasit(id);
  }, [prihlasit, odhlasit, id, popis]);
}

/**
 * Totéž pro obrazovky, kde se všechny změny dělají jednou funkcí (`zmenit`, `upravit`): formulář je
 * rozepsaný od chvíle, kdy do něj uživatel sáhl, a přestane být po uložení nebo zahození.
 *
 * ```tsx
 * const rozepsane = useZnackaRozepsaneho(`zakázka ${z.cislo}`);
 * const zmenit = (zmena) => { rozepsane.oznacit(); setF(…); };
 * // po úspěšném uložení: rozepsane.hotovo();
 * ```
 */
export function useZnackaRozepsaneho(popis: string | null | undefined): {
  /** Uživatel do formuláře sáhl — od teď je rozepsaný. */
  oznacit: () => void;
  /**
   * Uloženo nebo zahozeno — rozepsaný už není. Odhlásí se **hned** (ne až po vykreslení), takže přechod na novou
   * obrazovku zavolaný hned za `hotovo()` se hlídače odchodu neptá (05/473).
   */
  hotovo: () => void;
} {
  const kontext = useContext(Kontext);
  const id = useId();
  const [sahnuto, setSahnuto] = useState(false);
  const prihlasit = kontext?.prihlasit;
  const odhlasit = kontext?.odhlasit;
  const text = sahnuto ? (popis ?? null) : null;

  useEffect(() => {
    if (!prihlasit || !odhlasit || !text) return;
    prihlasit(id, text);
    return () => odhlasit(id);
  }, [prihlasit, odhlasit, id, text]);

  return useMemo(
    () => ({
      oznacit: () => setSahnuto(true),
      hotovo: () => {
        setSahnuto(false);
        odhlasit?.(id);
      },
    }),
    [odhlasit, id],
  );
}

/**
 * Značka pro obrazovku, která se ukládá sama (automatické ukládání nabídky): neuložené sleduje sama a hlásí ho
 * **hned** — `oznacit()` přihlásí, `hotovo()` odhlásí bez čekání na vykreslení (stav a ukládání se mění i mimo
 * události, po odpovědi serveru). `ulozit` = „uložit hned“: hlídač odchodu ho zavolá dřív, než se zeptá; vrátí‑li
 * `true` a obrazovka se mezitím odhlásila (nic neuloženého nezůstalo), odchod projde bez dialogu. `zahodit` hlídač
 * zavolá, když člověk v dialogu zvolí Zahodit (co čeká na uložení, už neodejde).
 *
 * ```tsx
 * const znacka = useZnackaSUlozenim('neuložené změny nabídky', ulozitHned, zahodit);
 * // něco čeká na uložení: znacka.oznacit(); všechno uloženo nebo zahozeno: znacka.hotovo();
 * ```
 */
export function useZnackaSUlozenim(
  popis: string,
  ulozit: () => Promise<boolean>,
  zahodit?: () => void,
): { oznacit: () => void; hotovo: () => void } {
  const kontext = useContext(Kontext);
  const id = useId();
  const ulozitRef = useRef(ulozit);
  ulozitRef.current = ulozit;
  const zahoditRef = useRef(zahodit);
  zahoditRef.current = zahodit;
  const oznaceno = useRef(false);
  const zivy = useRef(true);
  const prihlasit = kontext?.prihlasit;
  const odhlasit = kontext?.odhlasit;

  // Obrazovka zmizela (odchod prošel) — nic rozepsaného po ní nezůstává, ani když úklid jejích polí (efekty dětí
  // běží až po tomhle) ještě zavolá `oznacit`.
  useEffect(() => {
    zivy.current = true;
    return () => {
      zivy.current = false;
      if (oznaceno.current) odhlasit?.(id);
      oznaceno.current = false;
    };
  }, [odhlasit, id]);

  return useMemo(
    () => ({
      oznacit: () => {
        if (!zivy.current) return;
        oznaceno.current = true;
        prihlasit?.(id, popis, {
          ulozit: () => ulozitRef.current(),
          zahodit: () => zahoditRef.current?.(),
        });
      },
      hotovo: () => {
        oznaceno.current = false;
        odhlasit?.(id);
      },
    }),
    [prihlasit, odhlasit, id, popis],
  );
}

/** Co je zrovna rozepsané (pruh nové verze, dialogy). Bez poskytovatele prázdný seznam. */
export function useRozepsane(): readonly string[] {
  return useContext(Kontext)?.popisy ?? [];
}

/** „máte rozepsanou zakázku ZAK‑2026‑001“ — jedna věta z výčtu pro dialog i pro hlášku. */
export function vetaRozepsanych(popisy: readonly string[]): string {
  if (popisy.length === 0) return '';
  if (popisy.length === 1) return `Máte rozepsané: ${popisy[0]}.`;
  return `Máte rozepsané: ${popisy.slice(0, -1).join(', ')} a ${popisy[popisy.length - 1]}.`;
}

/**
 * Hlídač odchodu na jinou obrazovku (rozhodnutí zadavatele 24. 9. 2026, 05/473): když je něco rozepsané
 * a uživatel klikne jinam v aplikaci (lišta, odkaz, Zpět), navigace se zastaví a aplikační dialog se zeptá,
 * jestli rozepsané zahodit. Zahodit = navigace pokračuje, Zůstat = zůstane se na formuláři. Obrazovky, které se
 * ukládají samy (`useZnackaSUlozenim`), nejdřív zkusí uložit (nejvýš `LIMIT_ULOZENI_PRED_ODCHODEM_MS`) — uloží‑li se
 * všechno, odchod projde bez otázky.
 * Vykresluje se jednou uvnitř routeru (`CestyAplikace`); bez datového routeru (testy s `MemoryRouter`) nic nedělá.
 */
export function HlidacOdchodu() {
  const datovyRouter = useContext(UNSAFE_DataRouterContext);
  const kontext = useContext(Kontext);
  return datovyRouter && kontext ? <BlokatorOdchodu kontext={kontext} /> : null;
}

/**
 * Odchod na jinou obrazovku = jiná cesta a přechod, který udělal člověk (`PUSH` — odkaz, lišta; `POP` — Zpět
 * a Vpřed prohlížeče). Přesměrování aplikace s nahrazením historie (`REPLACE` — po uložení, po zavření zásuvky)
 * ani změna parametrů adresy (`?faktura=`, filtry) se nepočítá (05/473). Čistá funkce (unit testy).
 */
export function jeOdchod(
  z: { pathname: string },
  na: { pathname: string },
  akce: 'PUSH' | 'REPLACE' | 'POP' | string,
): boolean {
  return akce !== 'REPLACE' && z.pathname !== na.pathname;
}

function BlokatorOdchodu({ kontext }: { kontext: KontextRozepsanych }) {
  const dialog = useDialog();
  const { aktualni, ukladani } = kontext;
  const blokovat = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation, historyAction }) =>
      !odchodPovolen &&
      aktualni().length > 0 &&
      jeOdchod(currentLocation, nextLocation, historyAction),
    [aktualni],
  );
  const blocker = useBlocker(blokovat);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  // Rozhodnutí, které právě běží. Fáze `ukladam` (čeká se na „uložit hned“): další zastavení mezitím (druhé klepnutí,
  // Zpět) nic nespouští — rozhodnutí na konci platí pro poslední zastavení (`blockerRef`). Fáze `dialog`: dialog
  // hlídače je otevřený, nebo ho mezitím nahradil jiný dialog (Zavřít obrazovky klepnuté během ukládání se zeptá po
  // svém) — slib nahrazeného dialogu se nikdy nevyřídí, proto se další zastavení zeptá znovu a navigace, která mezitím
  // prošla jinudy (stav `unblocked`), rozhodnutí ukončí. Jinak by hlídač až do obnovení stránky mlčel a odkazy
  // s rozepsaným formulářem nedělaly nic.
  const rozhodnuti = useRef<{ faze: 'ukladam' | 'dialog' } | null>(null);
  const odpojeno = useRef(false);
  useEffect(() => {
    odpojeno.current = false;
    return () => {
      odpojeno.current = true;
    };
  }, []);

  useEffect(() => {
    if (blocker.state === 'unblocked') {
      rozhodnuti.current = null;
      return;
    }
    if (blocker.state !== 'blocked' || rozhodnuti.current?.faze === 'ukladam') return;
    // Po dialogu, který nahradil jiný, se ptá rovnou — uložit se už jednou nepovedlo.
    const toto: { faze: 'ukladam' | 'dialog' } = {
      faze: rozhodnuti.current ? 'dialog' : 'ukladam',
    };
    rozhodnuti.current = toto;
    const platne = () =>
      !odpojeno.current && rozhodnuti.current === toto && blockerRef.current.state === 'blocked';
    void (async () => {
      try {
        // Obrazovka, která se ukládá sama, nejdřív zkusí uložit (i to, co čeká na odklad) — dialog by jinak říkal
        // „neuložené se zahodí“, zatímco ukládání ještě běží. Čeká se jen omezeně; uvázlé ukládání = zeptat se.
        const ukl = toto.faze === 'ukladam' ? ukladani() : [];
        let neskoncilo = false;
        if (ukl.length > 0) {
          let casovac: number | undefined;
          const vysledky = await Promise.race([
            Promise.all(ukl.map((u) => u.ulozit().catch(() => false))),
            new Promise<null>((hotovo) => {
              casovac = window.setTimeout(() => hotovo(null), LIMIT_ULOZENI_PRED_ODCHODEM_MS);
            }),
          ]);
          window.clearTimeout(casovac);
          if (!platne()) return;
          if (vysledky?.every(Boolean) && aktualni().length === 0) {
            blockerRef.current.proceed?.();
            return;
          }
          neskoncilo = vysledky === null;
        }
        toto.faze = 'dialog';
        const vysledek = await dialog.potvrdit({
          titul: 'Máte rozepsaný formulář',
          text: [
            vetaRozepsanych(aktualni()),
            neskoncilo ? 'Ukládání ještě neskončilo (slabé spojení?).' : '',
            'Když přejdete jinam, neuložené se zahodí.',
          ]
            .filter(Boolean)
            .join(' '),
          tlacitko: 'Zahodit',
          zrusit: 'Zůstat',
          nebezpecne: true,
        });
        if (!platne()) return;
        const b = blockerRef.current;
        if (vysledek === null) {
          b.reset?.();
          return;
        }
        // Zahodit: obrazovky, které se ukládají samy, už nic dalšího neposílají (ani po odchodu).
        for (const u of ukladani()) u.zahodit?.();
        b.proceed?.();
      } finally {
        if (rozhodnuti.current === toto) rozhodnuti.current = null;
      }
    })();
    // Jednou na každé zastavení navigace: router dává každému zastavení nový objekt. Stav sám nestačí — „Zůstat“
    // a hned další klepnutí React vykreslí najednou (router stav mění v přechodu) a stav zůstane „blocked“.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker]);

  return null;
}

/** Otázka při zavření okna s rozepsaným obsahem (křížek, Escape) — stejná slova jako u hlídače odchodu. */
export function otazkaZahoditRozepsane(popis: string) {
  return {
    titul: 'Zahodit rozepsané?',
    text: `${vetaRozepsanych([popis])} Když okno zavřete, neuložené se zahodí.`,
    tlacitko: 'Zahodit',
    zrusit: 'Zůstat',
    nebezpecne: true,
  };
}

/** Pole, jehož změna nemá z okna dělat „rozepsaný formulář“ (hledání, filtr) — `type="search"` nebo `data-nehlidat`. */
function jeRozepsaniPole(cil: EventTarget | null): boolean {
  if (!(cil instanceof HTMLElement)) return false;
  if (!/^(INPUT|TEXTAREA|SELECT)$/.test(cil.tagName)) return false;
  if (cil instanceof HTMLInputElement && cil.type === 'search') return false;
  return !cil.closest('[data-nehlidat]');
}

/** Dialog, pokud je nad oknem poskytovatel; jinak `null` (okno bez poskytovatele se při zavření neptá). */
function useDialogNepovinne(): ReturnType<typeof useDialog> | null {
  try {
    // Hook se volá vždy; výjimka jen hlásí chybějícího poskytovatele.
    return useDialog();
  } catch {
    return null;
  }
}

/**
 * Hlídání rozepsaného obsahu okna (Modal, Zasuvka). `rozepsano`: `undefined` = okno si hlídá samo (od první změny
 * pole je rozepsané), řetězec = popis rozepsaného (okno ví, kdy je rozepsané), `null` = nic rozepsaného (neptá se).
 * `zavrit` je pro křížek a Escape: při rozepsaném obsahu se zeptá „Zahodit rozepsané?“, jinak okno rovnou zavře.
 * `naZmenu` patří na `onChange` formuláře okna (změnu pole zachytí i z vnořených polí).
 */
export function useRozepsaneOkno(
  titul: string,
  rozepsano: string | null | undefined,
  naZavrit: () => void,
): { zavrit: () => void; naZmenu: (u: FormEvent) => void } {
  const dialog = useDialogNepovinne();
  const [sahnuto, setSahnuto] = useState(false);
  const popis = rozepsano === undefined ? (sahnuto ? `okno „${titul}“` : null) : rozepsano;
  useRozepsano(popis);
  const posledni = useRef({ naZavrit, popis, dialog });
  posledni.current = { naZavrit, popis, dialog };
  const ptamSe = useRef(false);
  const zivy = useRef(true);
  useEffect(() => {
    zivy.current = true;
    return () => {
      zivy.current = false;
    };
  }, []);

  const zavrit = useCallback(() => {
    const { naZavrit: zavrit, popis: p, dialog: d } = posledni.current;
    if (!p || !d) {
      zavrit();
      return;
    }
    if (ptamSe.current) return;
    ptamSe.current = true;
    void d.potvrdit(otazkaZahoditRozepsane(p)).then((vysledek) => {
      ptamSe.current = false;
      if (vysledek !== null && zivy.current) posledni.current.naZavrit();
    });
  }, []);

  // Po úspěšném zápisu (okno se uložilo a zůstalo otevřené) není nic rozepsané, dokud se znovu nezmění pole.
  useEffect(() => {
    if (rozepsano !== undefined || !sahnuto) return;
    return poslouchejZapisy(() => {
      if (zivy.current) setSahnuto(false);
    });
  }, [rozepsano, sahnuto]);

  const naZmenu = useCallback(
    (u: FormEvent) => {
      if (rozepsano === undefined && jeRozepsaniPole(u.target)) setSahnuto(true);
    },
    [rozepsano],
  );
  return { zavrit, naZmenu };
}
